import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import Pagination from '../components/Pagination';

export default function StoreList() {
  useAuthGuard();

  const [sp, setSp] = useSearchParams();
  const initStatus = sp.get('status') || '';

  const [status, setStatus] = useState(initStatus);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [cursorHistory, setCursorHistory] = useState([]);

  // Verify modal state
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    setSp(p, { replace: true });
  }, [status, setSp]);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = new URL(`${API_BASE_URL}/admin/stores`);
        url.searchParams.set('limit', '20');
        if (cursor) url.searchParams.set('cursor', cursor);
        if (status) url.searchParams.set('status', status);

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const list = data.data?.items || data.items || data.data || [];
        setItems(Array.isArray(list) ? list : []);
        setHasMore(data.data?.has_more ?? data.has_more ?? false);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [cursor, status]);

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setCursor('');
    setCursorHistory([]);
  };

  const handleNext = () => {
    if (items.length > 0) {
      const lastItem = items[items.length - 1];
      setCursorHistory((prev) => [...prev, cursor]);
      setCursor(lastItem.id || '');
    }
  };

  const handlePrev = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setCursor(prevCursor || '');
      return next;
    });
  };

  const openVerifyModal = (store) => {
    setVerifyTarget(store);
    setVerifyNotes('');
  };

  const handleVerify = async (verified) => {
    if (!verifyTarget) return;
    setVerifying(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${verifyTarget.id}/verify`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verified, notes: verifyNotes }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setItems((prev) =>
        prev.map((s) =>
          s.id === verifyTarget.id
            ? { ...s, owner_verified: verified, status: verified ? 'active' : s.status }
            : s
        )
      );
      setVerifyTarget(null);
    } catch (e) {
      alert('Verification failed: ' + e.message);
    } finally {
      setVerifying(false);
    }
  };

  const fmtDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const statusBadge = (s) => {
    const map = {
      active: 'success',
      pending: 'warning',
      archived: 'secondary',
    };
    return map[s] || 'secondary';
  };

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="mb-1">Stores</h2>
        <p className="text-muted mb-0">Manage store listings and verification</p>
      </div>

      {/* Status filter tabs */}
      <ul className="nav nav-tabs mb-4">
        {['', 'pending', 'active', 'archived'].map((s) => (
          <li className="nav-item" key={s}>
            <button
              className={`nav-link ${status === s ? 'active' : ''}`}
              onClick={() => handleStatusChange(s)}
            >
              {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          </li>
        ))}
      </ul>

      <div className="card shadow-sm border-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Status</th>
                <th>Verified</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center py-4">Loading...</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={6} className="text-danger py-4">{error}</td>
                </tr>
              )}
              {!loading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">No stores found</td>
                </tr>
              )}
              {!loading &&
                !error &&
                items.map((store) => (
                  <tr key={store.id}>
                    <td className="fw-medium">{store.name || '-'}</td>
                    <td className="small">{store.address || '-'}</td>
                    <td>
                      <span className={`badge bg-${statusBadge(store.status)}`}>
                        {store.status || '-'}
                      </span>
                    </td>
                    <td>
                      {store.owner_verified ? (
                        <span className="badge bg-success">Verified</span>
                      ) : (
                        <span className="badge bg-warning text-dark">Unverified</span>
                      )}
                    </td>
                    <td className="text-nowrap">{fmtDate(store.created_at)}</td>
                    <td>
                      {!store.owner_verified && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openVerifyModal(store)}
                        >
                          Verify
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <Pagination
            hasMore={hasMore}
            hasPrev={cursorHistory.length > 0}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
          />
        </div>
      </div>

      {/* Verify Modal */}
      {verifyTarget && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Verify Store</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setVerifyTarget(null)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Store: <strong>{verifyTarget.name}</strong>
                </p>
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={verifyNotes}
                    onChange={(e) => setVerifyNotes(e.target.value)}
                    placeholder="Verification notes (optional)"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setVerifyTarget(null)}
                  disabled={verifying}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleVerify(false)}
                  disabled={verifying}
                >
                  Reject
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => handleVerify(true)}
                  disabled={verifying}
                >
                  {verifying ? 'Processing...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
